import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BASE = "https://dailydish.in/api/admin/nudge-campaign";

// ── tiny style helpers ────────────────────────────────────────
const card = {
  background: "#fff", borderRadius: 14,
  boxShadow: "0 2px 12px #0001", padding: 24, marginBottom: 24,
};
const lbl = { fontWeight: 600, fontSize: 12, marginBottom: 3, display: "block", color: "#555" };
const inp = {
  width: "100%", padding: "8px 11px", borderRadius: 8,
  border: "1px solid #ddd", fontSize: 13, marginBottom: 10, boxSizing: "border-box",
};
const btn = (color = "#1e4a1e", extra = {}) => ({
  background: color, color: "#fff", border: "none", borderRadius: 8,
  padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
  marginRight: 8, ...extra,
});
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// add N calendar days to a YYYY-MM-DD string
function addDays(dateStr, n) {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().split("T")[0];
}

function dayLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return dayNames[d.getDay()];
}

// ─────────────────────────────────────────────────────────────
const NudgeCampaignAdmin = () => {
  const today = new Date().toISOString().split("T")[0];

  // ── config ──────────────────────────────────────────────────
  const [defaultText, setDefaultText] = useState("");
  const [defaultImageUrl, setDefaultImageUrl] = useState("");
  const [defaultImageFile, setDefaultImageFile] = useState(null);
  const [defaultPreview, setDefaultPreview] = useState("");
  const [configSaving, setConfigSaving] = useState(false);

  // ── live schedule (from server) ──────────────────────────────
  const [schedule, setSchedule] = useState([]);

  // ── stats ────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);

  // ── 7-day batch builder ──────────────────────────────────────
  const [batchStart, setBatchStart] = useState(today);
  // Array of 7: { imageFile, imageUrl, previewUrl, messageText }
  const initDays = () =>
    Array.from({ length: 7 }, () => ({
      imageFile: null, imageUrl: "", previewUrl: "", messageText: "",
    }));
  const [batchDays, setBatchDays] = useState(initDays());
  const [batchSaving, setBatchSaving] = useState(false);

  // ── single-day add ───────────────────────────────────────────
  const [singleDate, setSingleDate] = useState("");
  const [singleText, setSingleText] = useState("");
  const [singleUrl, setSingleUrl] = useState("");
  const [singleFile, setSingleFile] = useState(null);
  const [singlePreview, setSinglePreview] = useState("");
  const [singleSaving, setSingleSaving] = useState(false);

  // ── per-row inline edit ──────────────────────────────────────
  const [editId, setEditId] = useState(null);
  const [editFile, setEditFile] = useState(null);

  // ── misc ─────────────────────────────────────────────────────
  const [toast, setToast] = useState("");
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState("batch"); // "batch" | "single"

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3500);
  };

  // ── load ─────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    try {
      const [cfgRes, schedRes, statsRes] = await Promise.all([
        axios.get(`${BASE}/config`),
        axios.get(`${BASE}/schedule`),
        axios.get(`${BASE}/stats`),
      ]);
      setDefaultText(cfgRes.data.defaultMessageText || "");
      setDefaultImageUrl(cfgRes.data.defaultImageUrl || "");
      setDefaultPreview(cfgRes.data.defaultImageUrl || "");
      setSchedule(schedRes.data);
      setStats(statsRes.data);
    } catch {
      showToast("Failed to load data");
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── save default config ──────────────────────────────────────
  const saveConfig = async () => {
    setConfigSaving(true);
    try {
      let imgUrl = defaultImageUrl;
      if (defaultImageFile) {
        const fd = new FormData();
        fd.append("image", defaultImageFile);
        const r = await axios.post(`${BASE}/config/image`, fd);
        imgUrl = r.data.imageUrl;
        setDefaultImageUrl(imgUrl);
        setDefaultPreview(imgUrl);
      }
      await axios.put(`${BASE}/config`, { defaultMessageText: defaultText, defaultImageUrl: imgUrl });
      showToast("✅ Default config saved");
      loadAll();
    } catch { showToast("❌ Save failed"); }
    finally { setConfigSaving(false); }
  };

  // ── batch field helpers ──────────────────────────────────────
  const updateDay = (idx, field, value) => {
    setBatchDays((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleBatchFile = (idx, file) => {
    if (!file) return;
    updateDay(idx, "imageFile", file);
    updateDay(idx, "previewUrl", URL.createObjectURL(file));
  };

  // ── save 7-day batch ─────────────────────────────────────────
  const saveBatch = async () => {
    setBatchSaving(true);
    let saved = 0, failed = 0;
    try {
      for (let i = 0; i < 7; i++) {
        const date = addDays(batchStart, i);
        const day = batchDays[i];
        let imgUrl = day.imageUrl || null;

        try {
          // Upsert the entry first
          await axios.post(`${BASE}/schedule`, {
            date,
            messageText: day.messageText || null,
            imageUrl: imgUrl,
          });

          // Upload file if provided
          if (day.imageFile) {
            const updated = await axios.get(`${BASE}/schedule`);
            const entry = updated.data.find((s) => s.date === date);
            if (entry) {
              const fd = new FormData();
              fd.append("image", day.imageFile);
              const r = await axios.post(`${BASE}/schedule/${entry._id}/image`, fd);
              imgUrl = r.data.imageUrl;
            }
          }
          saved++;
        } catch {
          failed++;
        }

        // small delay between saves
        await new Promise((r) => setTimeout(r, 150));
      }

      showToast(`✅ Saved ${saved}/7 days${failed ? ` (${failed} failed)` : ""}`);
      setBatchDays(initDays());
      loadAll();
    } catch { showToast("❌ Batch save failed"); }
    finally { setBatchSaving(false); }
  };

  // ── single-day save ──────────────────────────────────────────
  const saveSingle = async () => {
    if (!singleDate) return showToast("Pick a date");
    setSingleSaving(true);
    try {
      let imgUrl = singleUrl || null;
      await axios.post(`${BASE}/schedule`, {
        date: singleDate, messageText: singleText || null, imageUrl: imgUrl,
      });
      if (singleFile) {
        const sched = await axios.get(`${BASE}/schedule`);
        const entry = sched.data.find((s) => s.date === singleDate);
        if (entry) {
          const fd = new FormData();
          fd.append("image", singleFile);
          await axios.post(`${BASE}/schedule/${entry._id}/image`, fd);
        }
      }
      showToast("✅ Scheduled");
      setSingleDate(""); setSingleText(""); setSingleUrl(""); setSingleFile(null); setSinglePreview("");
      loadAll();
    } catch { showToast("❌ Save failed"); }
    finally { setSingleSaving(false); }
  };

  // ── inline image update ──────────────────────────────────────
  const uploadEntryImage = async (entryId) => {
    if (!editFile) return showToast("Select a file");
    try {
      const fd = new FormData();
      fd.append("image", editFile);
      await axios.post(`${BASE}/schedule/${entryId}/image`, fd);
      showToast("✅ Image updated");
      setEditId(null); setEditFile(null);
      loadAll();
    } catch { showToast("❌ Upload failed"); }
  };

  // ── delete entry ─────────────────────────────────────────────
  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this scheduled message?")) return;
    try {
      await axios.delete(`${BASE}/schedule/${id}`);
      showToast("Deleted");
      loadAll();
    } catch { showToast("❌ Delete failed"); }
  };

  // ── manual run ───────────────────────────────────────────────
  const manualRun = async () => {
    if (!window.confirm("Send Campaign_Nudge right now to all eligible users?")) return;
    setRunning(true);
    try {
      const r = await axios.post(`${BASE}/run`);
      showToast(r.data.message || "Campaign started");
    } catch { showToast("❌ Failed"); }
    finally { setRunning(false); }
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  return (
    <div style={{ background: "#f5f5f5", minHeight: "100vh", padding: 24 }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 9999,
          background: toast.startsWith("✅") ? "#2e7d32" : toast.startsWith("❌") ? "#c62828" : "#1565c0",
          color: "#fff", padding: "12px 22px", borderRadius: 10,
          fontWeight: 600, fontSize: 14, boxShadow: "0 4px 16px #0003",
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontWeight: 800, color: "#1e4a1e", margin: 0 }}>
          📣 Campaign_Nudge
        </h2>
        <p style={{ color: "#888", margin: "4px 0 0", fontSize: 13 }}>
          8:30 PM IST · Sun–Fri · Excludes Sat · Excludes free-offer users · Opts out after 7 no-order reminders
        </p>
      </div>

      {/* Stats + Run Now */}
      {stats && (
        <div style={{ display: "flex", gap: 14, marginBottom: 24, flexWrap: "wrap", alignItems: "stretch" }}>
          {[
            ["Eligible Today", stats.totalEligible, "#1e4a1e"],
            ["Opted Out", stats.optedOut, "#b71c1c"],
            ["Excluded (free offer)", stats.withActiveOffer, "#1565c0"],
          ].map(([l, v, c]) => (
            <div key={l} style={{
              background: "#fff", borderRadius: 12, padding: "14px 22px",
              boxShadow: "0 2px 8px #0001", borderLeft: `4px solid ${c}`, minWidth: 160,
            }}>
              <div style={{ fontSize: 11, color: "#888" }}>{l}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: c }}>{v ?? "—"}</div>
            </div>
          ))}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
            <button onClick={manualRun} disabled={running} style={btn("#e65100")}>
              {running ? "Sending…" : "▶ Run Now"}
            </button>
          </div>
        </div>
      )}

      {/* ── Default config ── */}
      <div style={card}>
        <h5 style={{ fontWeight: 700, marginBottom: 14, color: "#1e4a1e" }}>
          🖼 Default Message
          <span style={{ fontSize: 12, fontWeight: 400, color: "#888", marginLeft: 8 }}>
            used on days with no scheduled override
          </span>
        </h5>

        <label style={lbl}>Message Text</label>
        <textarea value={defaultText} onChange={(e) => setDefaultText(e.target.value)}
          rows={3} style={{ ...inp, resize: "vertical" }}
          placeholder="Your meal for tomorrow isn't sorted yet 🥗&#10;Confirm before 10:00 PM" />

        <label style={lbl}>Image (URL or upload)</label>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <input type="text" value={defaultImageUrl}
              onChange={(e) => { setDefaultImageUrl(e.target.value); setDefaultPreview(e.target.value); }}
              style={inp} placeholder="https://…" />
            <input type="file" accept="image/*"
              onChange={(e) => {
                const f = e.target.files[0];
                setDefaultImageFile(f);
                if (f) setDefaultPreview(URL.createObjectURL(f));
              }} />
          </div>
          {defaultPreview && (
            <img src={defaultPreview} alt="" style={{ width: 110, height: 80, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }} />
          )}
        </div>

        <button onClick={saveConfig} disabled={configSaving} style={{ ...btn(), marginTop: 8 }}>
          {configSaving ? "Saving…" : "Save Default Config"}
        </button>
      </div>

      {/* ── Schedule section ── */}
      <div style={card}>
        <h5 style={{ fontWeight: 700, marginBottom: 4, color: "#1e4a1e" }}>📅 Scheduled Messages</h5>
        <p style={{ color: "#888", fontSize: 13, marginBottom: 16 }}>
          Each scheduled date overrides the default image/text automatically at 8:30 PM on that day.
          Saturday entries are stored but the cron never fires — safe to skip that day.
        </p>

        {/* Tab switcher */}
        <div style={{ display: "flex", gap: 0, marginBottom: 20, borderRadius: 10, overflow: "hidden", border: "1px solid #ddd", width: "fit-content" }}>
          {[["batch", "📆 Schedule 7 Days"], ["single", "➕ Add Single Day"]].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)} style={{
              padding: "9px 22px", fontWeight: 600, fontSize: 13, border: "none", cursor: "pointer",
              background: activeTab === key ? "#1e4a1e" : "#fff",
              color: activeTab === key ? "#fff" : "#555",
            }}>{label}</button>
          ))}
        </div>

        {/* ── BATCH: 7-day form ── */}
        {activeTab === "batch" && (
          <div style={{ background: "#f9f9f9", borderRadius: 10, padding: 20, border: "1px solid #eee" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div>
                <label style={lbl}>Start Date</label>
                <input type="date" min={today} value={batchStart}
                  onChange={(e) => setBatchStart(e.target.value)}
                  style={{ ...inp, width: 160, marginBottom: 0 }} />
              </div>
              <div style={{ fontSize: 13, color: "#888", marginTop: 18 }}>
                → will schedule <strong>{batchStart}</strong> through <strong>{addDays(batchStart, 6)}</strong>
              </div>
            </div>

            {/* 7-day grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
              {batchDays.map((day, i) => {
                const date = addDays(batchStart, i);
                const dName = dayLabel(date);
                const isSat = dName === "Sat";
                const existsAlready = schedule.some((s) => s.date === date);
                return (
                  <div key={i} style={{
                    background: "#fff", borderRadius: 10, padding: 14,
                    border: isSat ? "2px dashed #ffb300" : existsAlready ? "2px solid #1565c0" : "1px solid #eee",
                  }}>
                    {/* Day header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 8,
                        background: isSat ? "#fff8e1" : "#f0f7ee",
                        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                        fontWeight: 700, fontSize: 11, lineHeight: 1.2,
                        color: isSat ? "#f57f17" : "#1e4a1e",
                      }}>
                        <span>{dName}</span>
                        <span style={{ fontSize: 10 }}>{date.slice(5)}</span>
                      </div>
                      <div style={{ fontSize: 12 }}>
                        <strong>Day {i + 1}</strong>
                        {isSat && <span style={{ marginLeft: 6, color: "#f57f17", fontSize: 11 }}>Sat — cron skips</span>}
                        {existsAlready && <span style={{ marginLeft: 6, color: "#1565c0", fontSize: 11 }}>will overwrite</span>}
                      </div>
                    </div>

                    {/* Image */}
                    <label style={lbl}>Image</label>
                    <input type="text" value={day.imageUrl}
                      onChange={(e) => {
                        updateDay(i, "imageUrl", e.target.value);
                        updateDay(i, "previewUrl", e.target.value);
                      }}
                      style={{ ...inp, fontSize: 12 }} placeholder="URL (optional)" />
                    <input type="file" accept="image/*" style={{ fontSize: 12, marginBottom: 8 }}
                      onChange={(e) => handleBatchFile(i, e.target.files[0])} />
                    {day.previewUrl && (
                      <img src={day.previewUrl} alt="" style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 6, marginBottom: 8 }} />
                    )}

                    {/* Text */}
                    <label style={lbl}>Message Text <span style={{ fontWeight: 400, color: "#aaa" }}>(blank = default)</span></label>
                    <textarea rows={2} value={day.messageText}
                      onChange={(e) => updateDay(i, "messageText", e.target.value)}
                      style={{ ...inp, resize: "vertical", fontSize: 12 }}
                      placeholder="Leave blank to use default text" />
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 12 }}>
              <button onClick={saveBatch} disabled={batchSaving} style={btn()}>
                {batchSaving ? "Saving…" : "💾 Save All 7 Days"}
              </button>
              <button onClick={() => setBatchDays(initDays())} style={btn("#888")}>
                Clear
              </button>
              <span style={{ fontSize: 12, color: "#888" }}>
                Days with no image/text will use the default config.
              </span>
            </div>
          </div>
        )}

        {/* ── SINGLE: one-day form ── */}
        {activeTab === "single" && (
          <div style={{ background: "#f9f9f9", borderRadius: 10, padding: 20, border: "1px solid #eee" }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "0 0 150px" }}>
                <label style={lbl}>Date</label>
                <input type="date" min={today} value={singleDate}
                  onChange={(e) => setSingleDate(e.target.value)} style={inp} />
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label style={lbl}>Message Text (blank = default)</label>
                <textarea rows={2} value={singleText}
                  onChange={(e) => setSingleText(e.target.value)}
                  style={{ ...inp, resize: "vertical" }} placeholder="Leave blank to use default" />
              </div>
            </div>
            <label style={lbl}>Image</label>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <input type="text" value={singleUrl}
                  onChange={(e) => { setSingleUrl(e.target.value); setSinglePreview(e.target.value); }}
                  style={inp} placeholder="Paste URL (optional)" />
                <input type="file" accept="image/*"
                  onChange={(e) => { const f = e.target.files[0]; setSingleFile(f); if (f) setSinglePreview(URL.createObjectURL(f)); }} />
              </div>
              {singlePreview && (
                <img src={singlePreview} alt="" style={{ width: 110, height: 80, objectFit: "cover", borderRadius: 8 }} />
              )}
            </div>
            <button onClick={saveSingle} disabled={singleSaving} style={{ ...btn(), marginTop: 8 }}>
              {singleSaving ? "Saving…" : "Add Scheduled Day"}
            </button>
          </div>
        )}

        {/* ── Saved schedule table ── */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10, color: "#333" }}>
            All Scheduled Messages ({schedule.length})
          </div>
          {schedule.length === 0 ? (
            <div style={{ color: "#aaa", textAlign: "center", padding: 24, border: "1px dashed #ddd", borderRadius: 10 }}>
              No scheduled messages yet
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f0f7ee" }}>
                    {["Date", "Day", "Image", "Message", "Actions"].map((h) => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((entry) => {
                    const isPast = entry.date < today;
                    const isToday = entry.date === today;
                    const dName = dayLabel(entry.date);
                    const isSat = dName === "Sat";
                    return (
                      <tr key={entry._id} style={{ borderBottom: "1px solid #f0f0f0", opacity: isPast ? 0.5 : 1 }}>
                        <td style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {entry.date}
                          {isToday && <span style={{ marginLeft: 6, background: "#2e7d32", color: "#fff", borderRadius: 5, padding: "2px 6px", fontSize: 10 }}>TODAY</span>}
                          {isPast && <span style={{ marginLeft: 6, background: "#ccc", color: "#fff", borderRadius: 5, padding: "2px 6px", fontSize: 10 }}>PAST</span>}
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{
                            fontWeight: 700, fontSize: 12,
                            color: isSat ? "#f57f17" : "#1e4a1e",
                          }}>
                            {dName} {isSat && "⚠"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          {entry.imageUrl
                            ? <img src={entry.imageUrl} alt="" style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 6 }} />
                            : <span style={{ color: "#aaa", fontSize: 12 }}>default</span>}
                        </td>
                        <td style={{ padding: "10px 12px", maxWidth: 200, fontSize: 12, color: entry.messageText ? "#333" : "#aaa" }}>
                          {entry.messageText || "default"}
                        </td>
                        <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                          {editId === entry._id ? (
                            <span style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                              <input type="file" accept="image/*" style={{ fontSize: 11 }}
                                onChange={(e) => setEditFile(e.target.files[0])} />
                              <button onClick={() => uploadEntryImage(entry._id)} style={btn("#1565c0", { padding: "6px 12px", fontSize: 12 })}>Upload</button>
                              <button onClick={() => { setEditId(null); setEditFile(null); }} style={btn("#888", { padding: "6px 12px", fontSize: 12 })}>✕</button>
                            </span>
                          ) : (
                            <>
                              <button onClick={() => setEditId(entry._id)} style={btn("#1565c0", { padding: "6px 12px", fontSize: 12 })}>📷</button>
                              <button onClick={() => deleteEntry(entry._id)} style={btn("#c62828", { padding: "6px 12px", fontSize: 12 })}>🗑</button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Info */}
      <div style={{ ...card, background: "#f0f7ee", border: "1px solid #c8e6c9" }}>
        <h6 style={{ fontWeight: 700, color: "#1e4a1e", marginBottom: 10 }}>ℹ How it works</h6>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "#444", lineHeight: 2.1 }}>
          <li>Cron fires at <strong>8:30 PM IST, Sunday–Friday</strong> — Saturday is always skipped.</li>
          <li>If a scheduled message exists for <strong>today's date</strong>, that image/text is used — otherwise the default.</li>
          <li>Targets users registered in the <strong>last 2 months</strong> who haven't ordered for tomorrow.</li>
          <li>Users with an active free-offer (<em>hubOffer</em>) are excluded — they get <em>Checkout_new</em>.</li>
          <li>After <strong>7 consecutive reminders with no order</strong>, the user is auto opted-out.</li>
          <li>Placing an order resets the counter (call <code>resetNudgeCount</code> on order creation).</li>
          <li>AiSensy campaign name: <code>Campaign_Nudge</code></li>
        </ul>
      </div>
    </div>
  );
};

export default NudgeCampaignAdmin;
